/**
 * useMorningSync — fetches the day pack into IndexedDB on app open.
 *
 * Triggers when:
 * - App mounts + user is online + last sync > 4h ago (or never)
 * - User explicitly calls `syncNow()`
 *
 * Syncs: missions, leads (via mission leads), appointments, week plan.
 * After sync, updates `lastSyncAt` state for UI display.
 *
 * Diagnostic events emitted to scout_activity_log:
 * - sync_start: { email, role, trigger }
 * - sync_complete: { mission_count, lead_count, appointment_count, duration_ms }
 * - sync_error: { error, duration_ms }
 * - sync_skipped: { reason, last_sync_at, cache_age_ms }
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { get } from '@appic/lib/http/serviceLayerClient'
import { DATA_CLASS } from './dataClasses'
import {
  setOfflineMissions,
  setOfflineLeads,
  setOfflineAppointments,
  setOfflineWeekPlan,
  setOfflineRoutes,
  setLastSyncTimestamp,
  getLastSyncTimestamp,
  pruneStaleOfflineData,
  startOfflineQueue,
  startUnloadFlush,
  isConfirmedOffline,
} from '@appic/lib'
import { missionsGateway } from '@appic/lib/ui/missionsGateway'
import { listAppointments } from '@appic/lib/api/appointments'
import { getRouteVariants } from '@appic/lib/api/missionVisits'
import type { OptimizeRouteResult } from '@appic/lib/api/missionVisits.types'
import { missionService } from '@/services/missionService'
import type { Mission, MissionLead } from '@appic/lib/api/missions'
import { usePermissions } from './usePermissions'
import { logScoutEvent } from '@appic/lib/utils/scoutLog'

const SYNC_INTERVAL_MS = 4 * 60 * 60 * 1000 // 4 hours

export interface MorningSyncResult {
  lastSyncAt: string | null
  isSyncing: boolean
  syncError: string | null
  syncNow: () => Promise<void>
}

export function useMorningSync(
  email: string | null | undefined,
  role: string | null | undefined,
  bundesland?: string,
): MorningSyncResult {
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const syncingRef = useRef(false)
  const queryClient = useQueryClient()

  const { canAdmin, canWrite, isLegacyMode } = usePermissions()

  // Map permissions to API role. Admin-level can see all missions; write-level sees own/team.
  // In legacy mode (no permission map), fall back to a conservative mapping based on the legacy role string.
  const legacyRole = (role ?? '').toLowerCase()
  const isAdminLike =
    legacyRole === 'admin' ||
    legacyRole === 'manager' ||
    legacyRole === 'superadmin' ||
    legacyRole === 'sales_manager'
  const isSalesLike =
    legacyRole === 'sales' ||
    legacyRole === 'field_sales' ||
    legacyRole === 'handelsvertreter' ||
    legacyRole === 'caller'

  const apiRole: 'Admin' | 'Sales' | 'Viewer' = isLegacyMode
    ? (isAdminLike ? 'Admin' : isSalesLike ? 'Sales' : 'Viewer')
    : (canAdmin('scout') ? 'Admin' : canWrite('scout') ? 'Sales' : 'Viewer')

  const doSync = useCallback(async (trigger: 'auto' | 'manual' | 'mount' = 'auto') => {
    if (!email) {
      logScoutEvent('sync_skipped', { reason: 'no_email' })
      return
    }
    if (syncingRef.current) {
      logScoutEvent('sync_skipped', { reason: 'already_syncing' })
      return
    }
    // Probe truth, not the OS link-layer flag. This skip used to fire on
    // `navigator.onLine`, which read FALSE on a live network — so the morning sync
    // silently no-op'd on a working connection for exactly the same reason the
    // visit-report POST was never issued (BUG-2026-07-14-VC559). `isConfirmedOffline`
    // is true ONLY when a probe actually failed; an un-probed `unknown` still syncs.
    if (isConfirmedOffline()) {
      logScoutEvent('sync_skipped', { reason: 'offline' })
      return
    }

    syncingRef.current = true
    setIsSyncing(true)
    setSyncError(null)
    const syncStart = Date.now()

    logScoutEvent('sync_start', { email, role: apiRole, trigger })

    try {
      // Never-dark (P3 "stale data poses as live"): these reads are NOT
      // swallowed. A failed read MUST reject so it lands in the catch below —
      // surfacing on `syncError` and leaving the previous offline cache +
      // lastSyncAt untouched — instead of being coerced to an empty array/null
      // that poses as "no appointments / no leads / no route" and then gets
      // written over the good cache while the sync stamp advances.
      // Fetch missions + leads in parallel with appointments
      const [missions, appointments] = await Promise.all([
        missionsGateway.listMissions(email, apiRole),
        listAppointments({ owner_email: email }),
      ])

      // Fetch leads for all missions
      const leadMap = new Map<string, MissionLead>()
      if (missions.length > 0) {
        const leadLists = await Promise.all(
          missions.map(m => missionsGateway.listMissionLeads(m.id))
        )
        for (const list of leadLists) {
          for (const ml of list) {
            if (ml.leadId) leadMap.set(ml.leadId, ml)
          }
        }
      }

      // Read cached route variants (GET — no Google API calls).
      // optimizeRoute (POST) is only called on explicit user action, never on mount.
      const routeMap: Record<string, OptimizeRouteResult> = {}
      if (missions.length > 0) {
        // getRouteVariants returns null only for a genuine "no cached variant
        // yet" (404); a real read failure rejects and propagates to the catch.
        const routeResults = await Promise.all(
          missions.map(m => getRouteVariants(m.id))
        )
        for (let i = 0; i < missions.length; i++) {
          const route = routeResults[i]
          if (route) routeMap[missions[i].id] = route
        }
      }

      // Store everything in IndexedDB
      await Promise.all([
        setOfflineMissions<Mission[]>(email, missions),
        setOfflineLeads<[string, MissionLead][]>(email, Array.from(leadMap.entries())),
        setOfflineAppointments(email, appointments),
        setOfflineRoutes(email, routeMap),
      ])

      // Week plan (if bundesland known)
      if (bundesland) {
        try {
          const weekData = await missionService.fetchWeeklyPlanningData(bundesland)
          await setOfflineWeekPlan(email, bundesland, weekData)
        } catch {
          // non-critical — week plan is optional
        }
      }

      // ADR-037: Prefetch all page contexts into React Query cache → IDB
      // This ensures every page renders instantly from cache, even on 3G/offline.
      // Use today's date for tagesplanung so the cache key matches the page's query
      const todayISO = new Date().toISOString().split('T')[0]
      const contextPrefetches = [
        queryClient.prefetchQuery({
          queryKey: ['tagesplanung-context', email, apiRole, todayISO],
          queryFn: () => get(`/v1/scout/tagesplanung/context?email=${encodeURIComponent(email)}&role=${apiRole}&date=${todayISO}`).then(r => r.data),
          staleTime: DATA_CLASS.SESSION,
        }),
        queryClient.prefetchQuery({
          queryKey: ['wochenplanung-context', email, apiRole, undefined],
          queryFn: () => get(`/v1/scout/wochenplanung/context?email=${encodeURIComponent(email)}&role=${apiRole}`).then(r => r.data),
          staleTime: DATA_CLASS.SESSION,
        }),
        queryClient.prefetchQuery({
          queryKey: ['prequal-context', email, undefined],
          queryFn: () => get(`/v1/scout/prequal/context?email=${encodeURIComponent(email)}`).then(r => r.data),
          staleTime: DATA_CLASS.SESSION,
        }),
      ]
      await Promise.allSettled(contextPrefetches)

      await setLastSyncTimestamp(email)
      setLastSyncAt(new Date().toISOString())

      // Prune stale data older than 48h
      await pruneStaleOfflineData()

      logScoutEvent('sync_complete', {
        mission_count: missions.length,
        lead_count: leadMap.size,
        appointment_count: appointments.length,
        duration_ms: Date.now() - syncStart,
        trigger,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync fehlgeschlagen'
      setSyncError(message)
      logScoutEvent('sync_error', {
        error: message,
        duration_ms: Date.now() - syncStart,
        trigger,
      })
    } finally {
      syncingRef.current = false
      setIsSyncing(false)
    }
  }, [email, apiRole, bundesland, queryClient])

  // On mount: check if sync is needed + start offline queue
  useEffect(() => {
    if (!email) return

    startOfflineQueue()
    // Best-effort durable beacon of the pending outbox on pagehide/hide, alongside the
    // background flush (FR-FE-IRONCLAD-TELEMETRY-OUTBOX). Idempotent — safe next to
    // AppLayout's call; whichever mounts first wins, the other no-ops.
    startUnloadFlush()

    getLastSyncTimestamp(email).then(ts => {
      const lastSync = ts !== null && ts !== undefined ? ts : null
      setLastSyncAt(lastSync)

      // Probe truth, not the flag (see doSync above).
      if (isConfirmedOffline()) {
        logScoutEvent('sync_skipped', { reason: 'offline', last_sync_at: lastSync })
        return
      }

      // Sync if never synced or last sync > 4h ago
      if (!ts) {
        doSync('mount')
        return
      }
      const age = Date.now() - new Date(ts).getTime()
      if (age > SYNC_INTERVAL_MS) {
        doSync('mount')
      } else {
        logScoutEvent('sync_skipped', {
          reason: 'interval_not_reached',
          last_sync_at: ts,
          cache_age_ms: age,
          threshold_ms: SYNC_INTERVAL_MS,
          next_sync_in_ms: SYNC_INTERVAL_MS - age,
        })
      }
    })
  }, [email, doSync])

  return {
    lastSyncAt,
    isSyncing,
    syncError,
    syncNow: () => doSync('manual'),
  }
}
